/**
 * Routes a question about one specific record — an invoice, a booking, a piece
 * of equipment, an account — to the skill that looks that record up.
 *
 * ## Why identifiers route before phrases
 *
 * Every other skill is picked by the words around the question, which works
 * because questions about a total are phrased in a handful of ways. Questions
 * about a named record are not: "why hasn't invoice 1234567 been paid out",
 * "invoice 1234567 status?", "can you check on invoice #1234567 for me" and
 * "was 1234567 refunded, the invoice" are one question with nothing in common
 * but the record. What they do share is the identifier, and an identifier is
 * unambiguous in a way a phrase never is — so it decides.
 *
 * That also stops a phrase-matched neighbour from taking a question it cannot
 * answer. "Why did payment fail on invoice 1234567" reads like the booking
 * payment-failure question, which would then search *bookings* for the invoice
 * number and report that no such booking exists.
 *
 * ## Why only labelled identifiers count
 *
 * A number only routes when it is labelled ("invoice 1234567", "booking
 * #1021445", "container ABCU1234567") or is unmistakably a shipping-container
 * number. A bare "1234567" could be any of them, and guessing wrong would send
 * the user an answer about a different record than the one they meant.
 * Unlabelled numbers are still read by each skill once the phrasing has picked
 * it — this only governs routing.
 */

/** Which lookup a message is asking for. */
export type LookupKind =
  | 'account'
  | 'invoice'
  | 'bookingInvoices'
  | 'booking'
  | 'bookingPaymentFailure'
  | 'equipment'
  | 'equipmentCheckout';

/** "number", "no.", "#", ":" and the like between a label and its value. */
const LABEL_GAP = '\\s*(?:(?:number|num|nbr|no\\.?)\\s*)?[:#]?\\s*';

const INVOICE_NUMBER = new RegExp(`\\b(?:invoice|inv)${LABEL_GAP}(\\d{4,})\\b`, 'i');

const BOOKING_NUMBER = new RegExp(
  `\\b(?:booking|order|reservation|confirmation)${LABEL_GAP}(\\d{4,})\\b`,
  'i',
);

/** The equipment token itself: letters and digits, at least one digit. */
const EQUIPMENT_TOKEN = '[a-z0-9-]*\\d[a-z0-9-]*';

const KEYED_EQUIPMENT = new RegExp(
  `\\b(?:container|trailer|chassis|equipment|unit|reefer)${LABEL_GAP}(${EQUIPMENT_TOKEN})\\b`,
  'gi',
);

/**
 * Fewer digits than this reads as a size ("53 trailer", "40ft container"), not
 * an equipment number, and a marketplace question about trailer length must not
 * be routed to an equipment lookup.
 */
const MIN_EQUIPMENT_DIGITS = 3;

/**
 * An ISO 6346 shipping-container number: a three-letter owner code, the
 * category letter U/J/Z, six digits and an optional check digit —
 * "ABCU1234567". Distinctive enough to route with no label.
 */
const ISO_CONTAINER = /\b([a-z]{3}[ujz])-?(\d{6})-?(\d)?\b/i;

/**
 * The same number written with spaces, "ABCU 123456 7". Only accepted in
 * capitals: lower-case, "thru 123456" would qualify.
 */
const SPACED_ISO_CONTAINER = /\b([A-Z]{3}[UJZ]) (\d{6}) ?(\d)?\b/;

const EMAIL = /[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/i;

/** "How do I cancel booking 1021445" is a how-to, not a lookup. */
const HOW_TO = /^\s*(?:how\s+(?:do|can|could|should|would)\s+(?:i|we)\b|how\s+to\b)/i;

/**
 * "Cancel booking 1021445" and "refund invoice 1234567" ask for something to be
 * done, which Sentry cannot do. Answering with the record's status instead would
 * read as though it had been done, so these are left to the how-to answers.
 * "Update on invoice 1234567" is a request for news, not an edit.
 */
const ACTION_REQUEST = new RegExp(
  '^\\s*(?:(?:please|pls|can you|could you|would you|will you|help me|' +
    "i (?:want|need|would like) to|i'd like to|i wanna)\\s+)*" +
    '(?:cancel|extend|modify|change|edit|approve|decline|reject|delete|remove|create|renew|' +
    'refund|void|reverse|resend|re-send|retry|rebook|terminate|end(?!\\s+dates?\\b)|stop|add|book|' +
    'update(?!\\s+on\\b))\\b',
  'i',
);

/**
 * Words that ask about the money on a booking rather than the booking itself —
 * "was booking 1021445 paid", "how much was I charged for booking 1021445".
 * "Payment method" and "charge overages" are settings of the booking, and stay
 * with the booking lookup.
 */
const INVOICE_WORDS = new RegExp(
  '\\b(?:invoices?|invoiced|charges?(?!\\s+overages?)|charged|billing|bills?|billed|' +
    'transactions?|payments?(?!\\s+method)|paid|pay|payouts?|refunds?|refunded|owed?|owes)\\b',
  'i',
);

const PAYMENT_WORDS = /\b(?:payment|charge|pay|card|ach)\b/i;
const FAILURE_WORDS =
  /\b(?:fail|failed|failing|failure|declined|denied|error|bounced|rejected)\b|\b(?:go|gone|went) through\b/i;

const CHECKOUT_WORDS =
  /\bcheck(?:ed|s)?[\s-]?out\b|\bcheckout\b|\bleft\b|\bleave\b|\bgated?[\s-]?out\b|\bpicked up\b/i;

/** Invoice number named in the message, e.g. "invoice #1234567" → "1234567". */
export const extractInvoiceNumber = (message: string): string | null =>
  message.match(INVOICE_NUMBER)?.[1] ?? null;

/** Booking (order) number named with a label, e.g. "booking 1021445". */
export const extractLabelledBookingNumber = (message: string): string | null =>
  message.match(BOOKING_NUMBER)?.[1] ?? null;

/**
 * Equipment number named with a label or written as a container number.
 * Returned upper-cased with any spacing removed, since that is how the gate
 * records it.
 */
export const extractLabelledEquipmentNumber = (message: string): string | null => {
  const iso = message.match(ISO_CONTAINER) ?? message.match(SPACED_ISO_CONTAINER);
  if (iso) return `${iso[1]}${iso[2]}${iso[3] ?? ''}`.toUpperCase();

  for (const match of Array.from(message.matchAll(KEYED_EQUIPMENT))) {
    const token = match[1];
    const digits = token.replace(/\D/g, '').length;
    if (digits >= MIN_EQUIPMENT_DIGITS) return token.toUpperCase();
  }
  return null;
};

/**
 * "How do I end my booking", "cancel booking 1021445": asking how to do
 * something, or asking for it to be done. No lookup or listing answers either.
 */
export const isHowToOrActionRequest = (message: string): boolean =>
  HOW_TO.test(message) || ACTION_REQUEST.test(message);

/** The first email address in the message, lower-cased. */
export const extractEmail = (message: string): string | null =>
  message.match(EMAIL)?.[0].toLowerCase() ?? null;

/** Words that make an email address a question about whose account it is. */
const ACCOUNT_WORDS =
  /\b(?:accounts?|users?|log(?:ged)?[\s-]?in|logins?|sign(?:ed)?[\s-]?(?:in|up)|registered|register|activated?|activation|deactivated?|disabled|enabled|locked|customer|company|who|whose|password)\b/i;

/** Words that add nothing to a bare "look up jane@acme.com". */
const LOOKUP_FILLER =
  /\b(?:look(?:ing)?|up|lookup|find|search|check|for|please|pls|the|an?|email|address|info|information|details|about|on|show|me|get|can|could|you|what|is|there)\b/gi;

/**
 * An email address names an account only when the question is about the
 * account. "Bookings for jane@acme.com" is a question about bookings that
 * happens to identify the buyer by email, and an account summary would not
 * answer it.
 */
const asksAboutAccount = (message: string): boolean => {
  if (ACCOUNT_WORDS.test(message)) return true;
  const rest = message.replace(EMAIL, ' ').replace(LOOKUP_FILLER, ' ');
  return rest.replace(/[^a-z0-9]/gi, '') === '';
};

/**
 * The lookup a message is asking for, or null when it names no record.
 *
 * Precedence follows how specific each identifier is: an invoice number names
 * exactly one invoice, even when the buyer's email is quoted alongside it; an
 * email names an account when the question is about the account; equipment is
 * asked about by its own number even when a booking is mentioned alongside it
 * ("is container X on booking 1021445"); and a booking number last, split three
 * ways by what is being asked about it.
 */
export const classifyLookup = (message: string): LookupKind | null => {
  if (isHowToOrActionRequest(message)) return null;

  if (extractInvoiceNumber(message)) return 'invoice';
  if (extractEmail(message) && asksAboutAccount(message)) return 'account';

  if (extractLabelledEquipmentNumber(message)) {
    return /\bwhen\b/i.test(message) && CHECKOUT_WORDS.test(message)
      ? 'equipmentCheckout'
      : 'equipment';
  }

  if (extractLabelledBookingNumber(message)) {
    if (PAYMENT_WORDS.test(message) && FAILURE_WORDS.test(message)) return 'bookingPaymentFailure';
    if (INVOICE_WORDS.test(message)) return 'bookingInvoices';
    return 'booking';
  }

  return null;
};

/** A `claims` predicate for a skill that answers one kind of lookup. */
export const claimsLookup =
  (kind: LookupKind) =>
  (message: string): boolean =>
    classifyLookup(message) === kind;
